import { AnimatePresence } from "framer-motion";
import { forEach, get, isEmpty, isArray, debounce } from "lodash";
import { useReducer, useCallback, useEffect } from "react";
import { withTranslation } from "react-i18next";

import { settings } from "@krea/common/settings";
import { Error } from "@krea/common/shared-components/fields/error";
import Fieldset from "@krea/common/shared-components/fields/fieldset";
import { FileInput } from "@krea/common/shared-components/fields/file/index";
import FileItem from "@krea/common/shared-components/fields/file-item";
import FilesList from "@krea/common/shared-components/fields/files-list";
import TextInput from "@krea/common/shared-components/fields/input";
import { Text } from "@krea/common/shared-components/text";
import {
  useDeleteFile,
  usePostFieldRequest,
} from "@krea/common/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/common/store/field-requests/hooks/queries";

import FreeTextParser from "../../freetext-parser";
import FieldRequestPreviewHeader from "../FieldRequestPreviewHeader";
import FieldRequestSaved from "../FieldRequestSaved";

const PropertyAdditionalDataForm = ({ fieldRequestID, isPreviewMode, t }) => {
  const { data: { displayName, submittedData } = {} } =
    useGetOneFieldRequest(fieldRequestID);

  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);
  const { mutate: deleteFile } = useDeleteFile();

  const errors = error?.data?.errors || [];
  const { property_valuation = "", files = [] } = submittedData;

  function reducer(state, action) {
    switch (action.type) {
      case "property_valuation":
        return { ...state, property_valuation: action.payload };
      case "touched":
        return { ...state, touched: action.payload };
      default:
        throw new Error();
    }
  }

  const [state, setState] = useReducer(reducer, {
    property_valuation,
    touched: false,
  });

  const value = state;

  const deleteFileFunc = (fileID) => {
    deleteFile({ fileID, fieldRequestID });
  };

  const createFormData = (value) => {
    const formData = new FormData();
    formData.append("property_valuation", value.property_valuation || "");

    if (isArray(value.files) && !isEmpty(value.files)) {
      forEach(value.files, (file) => {
        formData.append("files", file, file.name);
      });
    }

    return formData;
  };

  const onInputChange = (e, type) => {
    setState({ type: type, payload: e.target.value });
    setState({ type: "touched", payload: true });
  };

  const onBlur = (state, files = null) => {
    const { touched } = state;
    const value = state;

    value.files = isArray(files) ? files : null;
    const formData = createFormData(value);

    if (touched || isArray(files)) {
      postFieldRequest(formData);
      setState({ type: "touched", payload: false });
    }
  };

  const debouncedFunc = debounce((state) => onBlur(state), 2500);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback((state) => debouncedFunc(state), []);

  useEffect(() => {
    debouncedSave(state);

    return () => {
      debouncedFunc.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <>
      {isPreviewMode ? (
        <>
          {files && files.length > 0 && (
            <FieldRequestPreviewHeader
              label={displayName}
              value={
                <>
                  {files.map((file) => (
                    <FileItem file={file} isPreviewMode={isPreviewMode} />
                  ))}

                  {state.leasing_product_url && (
                    <InputLink
                      classNamePreview="mt-2"
                      isPreviewMode={isPreviewMode}
                      value={state.leasing_product_url}
                    />
                  )}
                </>
              }
            />
          )}
          {state.property_valuation && (
            <FieldRequestPreviewHeader
              subheader
              label={t("iaq.property.options.property_valuation")}
              value={<FreeTextParser text={state.property_valuation} />}
            />
          )}
        </>
      ) : (
        <>
          <form
            className="input-form mb-5"
            onBlur={() => onBlur(state)}
            onSubmit={(e) => e.preventDefault()}
          >
            <Fieldset>
              <Text size="md" className="font-weight-bold">
                {t("iaq.property.options.attachPropertyDocuments")}
              </Text>
              <div
                className="mb-2 p-4 border rounded"
                style={{ marginTop: "-12px" }}
              >
                <FileInput
                  multiple
                  id="proof-property"
                  files={files}
                  postFiles={(e) => onBlur(state, e)}
                  error={get(errors, "property_files[0].message", null)}
                />
                <AnimatePresence>
                  {files && !!files.length && (
                    <FilesList label={t("iaq.uploadedFiles")} className="mt-3">
                      {files.map((file) => (
                        <FileItem
                          file={file}
                          deleteFile={deleteFileFunc}
                          isPreviewMode={isPreviewMode}
                        />
                      ))}
                    </FilesList>
                  )}
                </AnimatePresence>

                {/* <TextInput
									type="text"
									inputMode="text"
									placeholder={t('iaq.property.options.linkToObject')}
									label={t('iaq.property.options.linkToObject')}
									className="mt-4"
								/> */}
              </div>

              <Text
                size="md"
                className="font-weight-bold"
                style={{ marginBottom: "-12px" }}
              >
                {t("iaq.property.options.property_valuation")}
              </Text>
              <TextInput
                type="text"
                inputMode="text"
                placeholder={t(
                  `iaq.property.options.property_valuation_placeholder.${settings.countryCode}`,
                  {
                    currency: settings.currency,
                  },
                )}
                value={value.property_valuation || ""}
                onChange={(e) => {
                  onInputChange(e, "property_valuation");
                }}
                error={
                  errors?.find((e) => e.field === "property_valuation")?.message
                }
              />
            </Fieldset>
          </form>

          <FieldRequestSaved fieldRequestID={fieldRequestID} />
        </>
      )}
    </>
  );
};

export default withTranslation()(PropertyAdditionalDataForm);
